export interface GooglePlaceResult {
  name: string;
  rating: number;
  userRatingsTotal: number;
  address?: string;
  location?: { lat: number, lng: number };
  accessibility?: string;
  isHandicapAccessible?: boolean;
  summary?: string;
  photoUrl?: string;
  photoUrls?: string[];
  phoneNumber?: string;
  website?: string;
  priceLevel?: number;
  openingHours?: string;
}

const CACHE_TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 jours
const cache: Record<string, { data: GooglePlaceResult | null, timestamp: number }> = {};

export async function fetchGooglePlaceData(name: string, address: string, phoneNumber?: string): Promise<GooglePlaceResult[]> {
  const cacheKey = `${name}_${address}_${phoneNumber || ''}`.toLowerCase();
  const cached = cache[cacheKey];
  
  if (cached && (Date.now() - cached.timestamp < CACHE_TTL_MS)) {
    return cached.data ? [cached.data] : [];
  }

  const MAX_RETRIES = 2;
  let attempt = 0;

  const executeWithTimeout = async () => {
    return await Promise.race([
      new Promise<GooglePlaceResult[]>(async (resolve, reject) => {
        if (!window.google || !window.google.maps) {
          console.error("Google Maps API not loaded");
          resolve([]);
          return;
        }

        console.log(`Fetching Google Place data for: ${name}, ${address} (Attempt ${attempt + 1})`);
        try {
          // Tentative d'utilisation de la nouvelle API Places (v2 / "New")
          const { Place } = await window.google.maps.importLibrary("places") as any;
          
          if (Place && typeof Place.searchByText === 'function') {
            const query = phoneNumber ? `${phoneNumber} ${address}` : `${name} ${address}`;
            let { places } = await Place.searchByText({
              textQuery: query,
              fields: [
                'id', 
                'displayName',
                'rating', 
                'userRatingCount', 
                'formattedAddress', 
                'location', 
                'accessibilityOptions', 
                'editorialSummary',
                'photos',
                'internationalPhoneNumber',
                'priceLevel',
                'regularOpeningHours'
              ],
              maxResultCount: 20
            });

            // Fallback: si pas de résultats, essayer juste avec le nom
            if (!places || places.length === 0) {
                const { places: placesByName } = await Place.searchByText({
                  textQuery: name,
                  fields: [
                    'id', 
                    'displayName',
                    'rating', 
                    'userRatingCount', 
                    'formattedAddress', 
                    'location', 
                    'accessibilityOptions', 
                    'editorialSummary',
                    'photos',
                    'internationalPhoneNumber',
                    'priceLevel',
                    'regularOpeningHours'
                  ],
                  maxResultCount: 20,
                });
                places = placesByName;
            }

            if (places && places.length > 0) {
              const formattedResults = places.map((place: any) => {
                // Récupération de l'accessibilité
                let accessibilityStr = "";
                let isHandicapAccessible = false;
                if (place.accessibilityOptions) {
                  const opts = place.accessibilityOptions;
                  const access = [];
                  if (opts.wheelchairAccessibleEntrance) {
                    access.push("Entrée accessible en fauteuil");
                    isHandicapAccessible = true;
                  }
                  if (opts.wheelchairAccessibleParking) access.push("Parking accessible en fauteuil");
                  if (opts.wheelchairAccessibleRestroom) access.push("Toilettes accessibles en fauteuil");
                  if (opts.wheelchairAccessibleSeating) access.push("Sièges accessibles en fauteuil");
                  accessibilityStr = access.join(", ");
                }

                // Récupération des photos
                let photoUrl = undefined;
                let photoUrls: string[] = [];
                if (place.photos && place.photos.length > 0) {
                  try {
                    photoUrls = place.photos.slice(0, 8).map((photo: any) => photo.getURI({ maxWidth: 1200 }));
                    photoUrl = photoUrls[0];
                  } catch (e) {
                    console.warn("Could not get photo URI:", e);
                  }
                }

                let openingHoursStr = undefined;
                if (place.regularOpeningHours && place.regularOpeningHours.weekdayDescriptions) {
                  openingHoursStr = place.regularOpeningHours.weekdayDescriptions.join('\n');
                }

                return {
                  name: place.displayName.text,
                  rating: place.rating || 0,
                  userRatingsTotal: place.userRatingCount || 0,
                  address: place.formattedAddress,
                  location: place.location ? { lat: place.location.lat(), lng: place.location.lng() } : undefined,
                  accessibility: accessibilityStr,
                  isHandicapAccessible: isHandicapAccessible,
                  summary: place.editorialSummary,
                  photoUrl: photoUrl,
                  photoUrls: photoUrls,
                  phoneNumber: place.internationalPhoneNumber,
                  website: place.websiteURI,
                  priceLevel: place.priceLevel,
                  openingHours: openingHoursStr
                };
              });
              resolve(formattedResults);
              return;
            }
          }
        } catch (error) {
          console.warn("Places API (New) failed, falling back to legacy PlacesService:", error);
        }

        // Fallback sur l'ancienne API PlacesService si la nouvelle n'est pas disponible ou échoue
        try {
          if (!window.google.maps.places) {
            resolve([]);
            return;
          }

          const service = new window.google.maps.places.PlacesService(document.createElement('div'));
          const searchRequest = {
            query: `${name} ${address}`,
          };

          service.textSearch(searchRequest, (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
              // We only fetch details for the first result to save API calls
              const firstResult = results[0];
              if (firstResult.place_id) {
                service.getDetails({
                  placeId: firstResult.place_id,
                  fields: ['name', 'rating', 'user_ratings_total', 'formatted_address', 'geometry', 'photos', 'international_phone_number', 'website', 'opening_hours', 'price_level']
                }, (place, detailStatus) => {
                  if (detailStatus === window.google.maps.places.PlacesServiceStatus.OK && place) {
                    let photoUrl = undefined;
                    let photoUrls: string[] = [];
                    
                    if (place.photos && place.photos.length > 0) {
                      try {
                        photoUrls = place.photos.slice(0, 8).map(photo => typeof photo.getUrl === 'function' ? photo.getUrl({ maxWidth: 1200 }) : '');
                        photoUrls = photoUrls.filter(url => url !== '');
                        if (photoUrls.length > 0) {
                          photoUrl = photoUrls[0];
                        }
                      } catch (e) {
                        console.warn("Could not get photo URL from legacy API:", e);
                      }
                    }

                    let openingHoursStr = undefined;
                    if (place.opening_hours && place.opening_hours.weekday_text) {
                      openingHoursStr = place.opening_hours.weekday_text.join('\n');
                    }

                    const formattedResult = {
                      name: place.name || name,
                      rating: place.rating || 0,
                      userRatingsTotal: place.user_ratings_total || 0,
                      address: place.formatted_address,
                      location: place.geometry?.location ? { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() } : undefined,
                      photoUrl: photoUrl,
                      photoUrls: photoUrls,
                      phoneNumber: place.international_phone_number,
                      website: place.website,
                      priceLevel: place.price_level,
                      openingHours: openingHoursStr
                    };
                    resolve([formattedResult]);
                  } else {
                    // Return basic result if details fail
                    resolve([formatLegacyResult(firstResult, name)]);
                  }
                });
              } else {
                resolve(results.map(r => formatLegacyResult(r, name)));
              }
            } else {
              resolve([]);
            }
          });
          
          function formatLegacyResult(result: any, fallbackName: string) {
            let photoUrl = undefined;
            let photoUrls: string[] = [];
            
            if (result.photos && result.photos.length > 0) {
              try {
                photoUrls = result.photos.slice(0, 8).map((photo: any) => typeof photo.getUrl === 'function' ? photo.getUrl({ maxWidth: 1200 }) : '');
                photoUrls = photoUrls.filter((url: string) => url !== '');
                if (photoUrls.length > 0) {
                  photoUrl = photoUrls[0];
                }
              } catch (e) {
                console.warn("Could not get photo URL from legacy API:", e);
              }
            }

            return {
              name: result.name || fallbackName,
              rating: result.rating || 0,
              userRatingsTotal: result.user_ratings_total || 0,
              address: result.formatted_address,
              location: result.geometry?.location ? { lat: result.geometry.location.lat(), lng: result.geometry.location.lng() } : undefined,
              photoUrl: photoUrl,
              photoUrls: photoUrls
            };
          }
        } catch (error) {
          console.error("Critical error in fetchGooglePlaceData:", error);
          resolve([]);
        }
      }),
      new Promise<GooglePlaceResult[]>((_, reject) => setTimeout(() => {
        reject(new Error("Google Places API timeout"));
      }, 30000)) // Augmenté à 30s
    ]);
  };

  let results: GooglePlaceResult[] = [];
  while (attempt <= MAX_RETRIES) {
    try {
      results = await executeWithTimeout();
      break; // Succès
    } catch (error) {
      console.warn(`Attempt ${attempt + 1} failed for ${name}:`, error);
      attempt++;
      if (attempt > MAX_RETRIES) {
        console.error(`All ${MAX_RETRIES + 1} attempts failed for ${name}. Returning empty results.`);
        results = [];
      } else {
        // Petit délai avant de réessayer
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }
  }

  cache[cacheKey] = { data: results[0] || null, timestamp: Date.now() };
  return results;
}
